/**
 * Infrastructure page — Dedicated AI Environment
 *
 * Shows:
 *  - VM status (Running / Stopped / Provisioning)
 *  - IP address
 *  - Start / Stop controls
 *  - "Provision VM" button if no VM yet (requires active subscription)
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { getAuth } from 'firebase/auth';
import { useAuth } from '../context/AuthContext';
import SeoHead from '../components/SeoHead';

// ============================================================================
// Types
// ============================================================================

interface VMInfo {
  instanceName: string;
  zone: string;
  machineType: string;
  status: string;
  ipAddress: string | null;
  createdAt: number | null;
  updatedAt: number | null;
}

// ============================================================================
// Helpers
// ============================================================================

function getProjectId(): string {
  return process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
}

function fnUrl(name: string): string {
  return `https://us-central1-${getProjectId()}.cloudfunctions.net/${name}`;
}

async function getAuthToken(): Promise<string | null> {
  const user = getAuth().currentUser;
  if (!user) return null;
  return user.getIdToken();
}

function formatDate(ms: number | null): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function machineTypeLabel(raw: string): string {
  const map: Record<string, string> = {
    'e2-standard-2': 'e2-standard-2 (2 vCPU / 8 GB RAM)',
    'e2-custom-4-8192': 'e2-custom-4-8192 (4 vCPU / 8 GB RAM)',
    'e2-standard-4': 'e2-standard-4 (4 vCPU / 16 GB RAM)',
    'e2-standard-8': 'e2-standard-8 (8 vCPU / 32 GB RAM)',
    'c3-standard-22': 'c3-standard-22 (22 vCPU / 88 GB RAM)',
    'n2d-standard-4': 'n2d-standard-4 (4 vCPU / 16 GB RAM)',
    'n1-standard-2': 'n1-standard-2 (2 vCPU / 7.5 GB RAM)',
  };
  return map[raw] || raw;
}

// ============================================================================
// Styled Components
// ============================================================================

const PageContainer = styled.main`
  padding: 1.5rem 1rem;
  width: 100%;
  margin: 0 auto;
  min-height: 100vh;
  background: var(--brand-canvas);

  @media (min-width: 768px) { padding: 2rem 1.5rem; }

  @media (min-width: 1024px) {
    max-width: 900px;
    padding: 2.5rem;
    min-height: calc(100vh - 160px);
  }

  @media (min-width: 1200px) { max-width: 1000px; }
`;

const PageTitle = styled.h1`
  font-family: 'Space Grotesk', 'Inter', -apple-system, sans-serif;
  font-size: 2rem;
  color: var(--brand-text);
  margin-bottom: 1.5rem;
  font-weight: 400;
  letter-spacing: -0.02em;
  text-transform: lowercase;

  @media (min-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 2rem;
  }
`;

const Section = styled.section`
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 0.375rem 1.25rem rgba(0, 0, 0, 0.04);
  border: 1px solid var(--brand-line);

  @media (min-width: 768px) {
    padding: 2rem;
    margin-bottom: 2rem;
    border-radius: 1.25rem;
  }
`;

const SectionLabel = styled.div`
  font-size: 0.7rem;
  text-transform: lowercase;
  letter-spacing: 0.08em;
  color: #AAA;
  font-weight: 600;
  margin-bottom: 1rem;
`;

type StatusVariant = 'running' | 'stopped' | 'provisioning' | 'error' | 'unknown';

const STATUS_STYLES: Record<StatusVariant, { bg: string; color: string; dot: string }> = {
  running:      { bg: '#F0FFF4', color: '#27AE60', dot: '#27AE60' },
  stopped:      { bg: '#F5F5F5', color: '#888',    dot: '#CCC' },
  provisioning: { bg: '#EDF7FF', color: '#2980B9', dot: '#2980B9' },
  error:        { bg: '#FFF0ED', color: '#C0392B', dot: '#C0392B' },
  unknown:      { bg: '#F5F5F5', color: '#888',    dot: '#CCC' },
};

function getStatusVariant(status: string): StatusVariant {
  const s = status.toUpperCase();
  if (s === 'RUNNING') return 'running';
  if (['STOPPED', 'TERMINATED'].includes(s)) return 'stopped';
  if (['PROVISIONING', 'STAGING', 'STOPPING', 'STARTING'].includes(s)) return 'provisioning';
  if (['DELETED', 'ERROR'].includes(s)) return 'error';
  return 'unknown';
}

const StatusBadge = styled.span<{ variant: StatusVariant }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  background: ${(p) => STATUS_STYLES[p.variant as StatusVariant].bg};
  color: ${(p) => STATUS_STYLES[p.variant as StatusVariant].color};
`;

const StatusDot = styled.span<{ variant: StatusVariant }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(p) => STATUS_STYLES[p.variant as StatusVariant].dot};
  display: inline-block;
  ${(p) => p.variant === 'provisioning' ? 'animation: pulse 1.4s infinite;' : ''}

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.35; }
  }
`;

const VMTitle = styled.div`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--brand-text);
  letter-spacing: -0.01em;
  margin-bottom: 0.25rem;
`;

const VMSubtitle = styled.div`
  font-size: 0.875rem;
  color: #888;
  margin-bottom: 1.25rem;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-top: 1px solid #F0F0F0;
  font-size: 0.875rem;
  color: #444;
`;

const DetailLabel = styled.span`
  color: #888;
`;

const IPBadge = styled.code`
  font-size: 0.85rem;
  background: var(--brand-surface);
  padding: 0.15rem 0.5rem;
  border-radius: 0.35rem;
  font-family: 'Courier New', monospace;
  color: var(--brand-text);
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1.5rem;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--brand-text);
  color: white;
  border: none;
  border-radius: 0.625rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s ease;

  &:hover:not(:disabled) { background: #333; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SecondaryButton = styled(PrimaryButton)`
  background: none;
  color: #555;
  border: 1px solid #D4D4D0;

  &:hover:not(:disabled) { background: var(--brand-surface); color: var(--brand-text); }
`;

const DangerButton = styled(PrimaryButton)`
  background: none;
  color: #C0392B;
  border: 1px solid #F5B7B1;

  &:hover:not(:disabled) { background: #FFF0ED; color: #A93226; }
`;

const FeatureList = styled.ul`
  list-style: none;
  margin: 1rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const FeatureItem = styled.li`
  font-size: 0.875rem;
  color: #444;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  &::before { content: '●'; color: #CCC; font-size: 0.5rem; }
`;

const ErrorText = styled.div`
  color: #C0392B;
  font-size: 0.875rem;
  margin-top: 0.75rem;
`;

const LoadingText = styled.div`
  color: #AAA;
  font-size: 0.875rem;
  padding: 1rem 0;
`;

const EstimatedCost = styled.div`
  font-size: 0.8rem;
  color: #AAA;
  margin-top: 0.5rem;
`;

// ============================================================================
// Component
// ============================================================================

const Infrastructure: React.FC = () => {
  const { user } = useAuth();

  const [vm, setVM] = useState<VMInfo | null | undefined>(undefined); // undefined = loading
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchVMStatus = useCallback(async () => {
    if (!user) return;
    try {
      const token = await getAuthToken();
      if (!token) return;
      const resp = await fetch(fnUrl('getVMStatus'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setVM(data.vm ?? null);
    } catch {
      setVM(null);
    }
  }, [user]);

  useEffect(() => {
    fetchVMStatus();
  }, [fetchVMStatus]);

  // Poll while VM is transitioning
  useEffect(() => {
    if (!vm) return;
    const transitioning = ['PROVISIONING', 'STAGING', 'STOPPING'].includes(vm.status);
    if (!transitioning) return;
    const id = setInterval(fetchVMStatus, 15_000);
    return () => clearInterval(id);
  }, [vm, fetchVMStatus]);

  const handleProvision = useCallback(async () => {
    setActionLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not signed in');
      const resp = await fetch(fnUrl('provisionUserVM'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        if (body?.code === 'SUBSCRIPTION_REQUIRED') {
          throw new Error('An active subscription is required to provision a VM. Subscribe on the Subscription page.');
        }
        throw new Error(body?.error || `HTTP ${resp.status}`);
      }
      await fetchVMStatus();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to provision VM');
    } finally {
      setActionLoading(false);
    }
  }, [fetchVMStatus]);

  const handleStop = useCallback(async () => {
    setActionLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not signed in');
      const resp = await fetch(fnUrl('stopVM'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      await fetchVMStatus();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to stop VM');
    } finally {
      setActionLoading(false);
    }
  }, [fetchVMStatus]);

  const handleStart = useCallback(async () => {
    setActionLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not signed in');
      // Wake seam first (idle-lifecycle, 2026-08-18): wakeWorkspaceVm keeps
      // BOTH Firestore planes consistent and refuses billing sleeps with an
      // honest message. Accounts without a routing doc (pure legacy uid-plane
      // VMs) fall back to the original startVM endpoint.
      const wakeResp = await fetch(fnUrl('wakeWorkspaceVm'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: 'button' }),
      });
      const wake = wakeResp.ok ? await wakeResp.json().catch(() => null) : null;
      if (wake?.state === 'suspended-billing' || wake?.state === 'archived') {
        setError(String(wake.message || 'Workstation cannot be started right now.'));
        return;
      }
      if (!wake || wake.state === 'no-workspace' || wake.state === 'error') {
        const resp = await fetch(fnUrl('startVM'), {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      }
      await fetchVMStatus();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to start VM');
    } finally {
      setActionLoading(false);
    }
  }, [fetchVMStatus]);

  const statusVariant = vm ? getStatusVariant(vm.status) : 'unknown';
  const isTransitioning = vm
    ? ['PROVISIONING', 'STAGING', 'STOPPING', 'STARTING'].includes(vm.status)
    : false;

  return (
    <PageContainer>
      <SeoHead
        title="Infrastructure — prest0"
        description="Enterprise-grade dedicated AI infrastructure for large law firms. SOC 2-ready, isolated cloud environments with end-to-end data sovereignty."
        canonicalUrl="https://prest0.ai/infrastructure"
        keywords="enterprise legal AI infrastructure, dedicated law firm AI environment, legal cloud infrastructure, SOC 2 legal AI, data sovereignty law firm, small firm AI infrastructure, solo lawyer cloud AI"
      />
      <PageTitle>infrastructure</PageTitle>

      <Section>
        <SectionLabel>Your Dedicated AI Environment</SectionLabel>

        {vm === undefined ? (
          <LoadingText>Loading…</LoadingText>
        ) : vm === null ? (
          <>
            <VMTitle style={{ color: '#AAA', fontWeight: 400 }}>No VM provisioned</VMTitle>
            <VMSubtitle>
              Provision Prest0n’s private secure workspace for your firm.
            </VMSubtitle>

            <FeatureList>
              <FeatureItem>Dedicated secure environment for your firm’s AI workflows</FeatureItem>
              <FeatureItem>e2-standard-2 — 2 vCPU, 8 GB RAM</FeatureItem>
              <FeatureItem>50 GB SSD boot disk — Ubuntu 22.04 LTS</FeatureItem>
              <FeatureItem>Stable always-on infrastructure separated from office devices</FeatureItem>
            </FeatureList>

            <EstimatedCost>Infrastructure cost is included inside the Starter plan experience</EstimatedCost>

            <ButtonRow>
              <PrimaryButton onClick={handleProvision} disabled={actionLoading}>
                {actionLoading ? 'Provisioning…' : 'Provision VM'}
              </PrimaryButton>
            </ButtonRow>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
              <VMTitle>{vm.instanceName}</VMTitle>
              <StatusBadge variant={statusVariant}>
                <StatusDot variant={statusVariant} />
                {vm.status}
              </StatusBadge>
            </div>
            <VMSubtitle>Prest0n’s private secure workspace for your firm</VMSubtitle>

            {vm.ipAddress && (
              <DetailRow>
                <DetailLabel>IP Address</DetailLabel>
                <IPBadge>{vm.ipAddress}</IPBadge>
              </DetailRow>
            )}
            <DetailRow>
              <DetailLabel>Machine Type</DetailLabel>
              <span>{machineTypeLabel(vm.machineType)}</span>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Zone</DetailLabel>
              <span>{vm.zone}</span>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Provisioned</DetailLabel>
              <span>{formatDate(vm.createdAt)}</span>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Last Updated</DetailLabel>
              <span>{formatDate(vm.updatedAt)}</span>
            </DetailRow>

            {isTransitioning && (
              <div style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: '#2980B9' }}>
                VM is transitioning — auto-refreshing every 15 seconds…
              </div>
            )}

            <ButtonRow>
              {vm.status === 'RUNNING' ? (
                <DangerButton onClick={handleStop} disabled={actionLoading || isTransitioning}>
                  {actionLoading ? 'Stopping…' : 'Stop VM'}
                </DangerButton>
              ) : vm.status === 'TERMINATED' || vm.status === 'STOPPED' ? (
                <PrimaryButton onClick={handleStart} disabled={actionLoading}>
                  {actionLoading ? 'Starting…' : 'Start VM'}
                </PrimaryButton>
              ) : null}
              <SecondaryButton onClick={fetchVMStatus} disabled={actionLoading}>
                Refresh Status
              </SecondaryButton>
            </ButtonRow>
          </>
        )}

        {error && <ErrorText>{error}</ErrorText>}
      </Section>
    </PageContainer>
  );
};

export default Infrastructure;
